/**
 * Note Chat View
 *
 * Main view for Note AI Edit with inline sections editing and streaming updates
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals/constants';
import SigningNoteOnboarding from '@components/Note/SigningNoteOnboarding';
import { Transcript } from '@components/Transcript';
import { CenteredSpinner } from '@components/ui/feedback/CenteredSpinner';
import { NotepadBox } from '@components/ui/NotepadBox';
import { useCreateClaimMutation } from '@features/billing/store/api';
import { useDebouncedWorkingBillingClaim,useGetEncounterQuery, useUpdateEncounterMutation } from '@features/encounters';
import type { PopoverSection } from '@features/encounters/components/SessionBillingPopover';
import { SessionBillingPopover } from '@features/encounters/components/SessionBillingPopover';
import type { EncounterAttestations, Modality } from '@features/encounters/types/attestations';
import {
  bareCodesToLineItems,
  buildDiagnosisCodeSources,
  extractBillingCodes,
  extractDiagnosisCodes,
  extractPlaceOfService,
} from '@features/encounters/utils/billingClaim';
import { useCSATTrigger } from '@features/feedback';
import { openChatWindow } from '@features/front-chat';
import {
  clearHighlightedSection,
  EncounterAnalysisSidebar,
  EncounterAnalysisSummary,
  NoteAuditSidebar,
  resetAuditUi,
  selectAuditSidebarMode,
  setSidebarMode,
  useAuditEnabled,
  useNoteAudit,
} from '@features/jotaudit';
import { useJotCoachEncounterSessionLazy } from '@features/jotcoach';
import {
  notesApi,
  useDeleteNoteMutation,
  useLazyGetSignedNotePdfQuery,
  useUpdateNoteSectionMutation,
} from '@features/notes';
import { getTemplateId, getTemplateName } from '@features/templates/utils';
import { useToolbar } from '@features/toolbar';
import {
  useFeatureFlag,
  useOnboardingStep,
  useUpdateOnboardingStep,
  useUser,
} from '@features/user';
import { MenuBook as AuditIcon } from '@mui/icons-material';
import { Alert, Box, Button, Fade, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEventTracking, useFeatureGate } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useModal } from '@store/hooks/useModal';
import { clearFindReplace } from '@store/slices/findReplaceSlice';
import { NoteTelemetryService, trackNoteTelemetry } from '@utils/analytics/noteTelemetry';
import type { AnyBlockNoteEditor } from '@utils/blocknote/types';
import { findReplaceInSections } from '@utils/findReplace';
import { useSnackbar } from 'notistack';

import { LAYOUT_SELECTORS, MOBILE_DRAWER_DIMENSIONS } from '../constants/layout';
import {
  useApprovalNavigation,
  useNoteCopy,
  useNoteGenerationStreaming,
  useNoteSectionSaveGuard,
  useNoteSigning,
  useNoteViewMode,
  useSSEPerformanceTracking,
} from '../hooks';
import {
  clearAllDirtySections,
  dismissChatOnboarding,
  initializeChat,
  setChatOnboardingVisibility,
  setChatVisibility,
} from '../store/chatSlice';
import { useNoteChatWithSections } from '../store/hooks';
import {
  selectActivitiesArray,
  selectCurrentStep,
  selectDirtySections,
  selectError,
  selectFirstTokenReceived,
  selectIsChatOnboardingVisible,
  selectIsChatVisible,
  selectNoteId,
  selectSectionPreviousContent,
  selectSelectedSectionIdsForCopy,
  selectStreamingContent,
} from '../store/selectors';

import { NoteChatLayout } from './layouts/NoteChatLayout';
import { NoteSectionContainer } from './layouts/NoteSectionContainer';
import { NoteTOCInline } from './NoteTOC/NoteTOCInline';
import { AddTemplateToListBanner } from './AddTemplateToListBanner';
import { ChatDiscoverabilityOverlay } from './ChatDiscoverabilityOverlay';
// import { ElevenLabsWidget } from './ElevenLabsWidget';
import { MobileChatDrawer } from './MobileChatDrawer';
import { NoteChat } from './NoteChat';
import { NoteGenerationProgress } from './NoteGenerationProgress';
import { NoteMetadata } from './NoteMetadata';
import { NoteSectionsList } from './NoteSectionsList';
import { NoteTabs } from './NoteTabs';
import { NoteTitleEditor } from './NoteTitleEditor';
import { NoteTOC } from './NoteTOC';
import { NoteTopBarCompleted } from './NoteTopBarCompleted';
import { NoteTopBarEdit } from './NoteTopBarEdit';
import { NoteTopBarTranscript } from './NoteTopBarTranscript';
import { SidebarContainer } from './SidebarContainer';

export const NoteChatView: React.FC = () => {
  const dispatch = useAppDispatch();
  const { noteId } = useParams<{ noteId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Store regeneration info in state (survives URL cleanup)
  // This is set from query params on initial load from template regeneration
  const [regenerationInfo, setRegenerationInfo] = useState<{
    isRegenerating: boolean;
    encounterId: string | null;
  } | null>(null);

  // Read regeneration query params on mount and store in state
  // ?generating=true&eid=<encounter_id>
  useEffect(() => {
    const isRegenerating = searchParams.get('generating') === 'true';
    const encounterId = searchParams.get('eid');

    if (isRegenerating && encounterId) {
      // Store the info in state
      setRegenerationInfo({ isRegenerating: true, encounterId });
      // Clear the URL params (so refresh doesn't restart regeneration)
      setSearchParams({}, { replace: true });
    }
  }, []); // Only run on mount

  const currentNoteId = useAppSelector(selectNoteId);
  const currentStep = useAppSelector(selectCurrentStep);
  const error = useAppSelector(selectError);
  const streamingContent = useAppSelector(selectStreamingContent);
  const firstTokenReceived = useAppSelector(selectFirstTokenReceived);
  const selectedSectionIds = useAppSelector(selectSelectedSectionIdsForCopy);
  const dirtySections = useAppSelector(selectDirtySections);
  const activities = useAppSelector(selectActivitiesArray);
  const isChatVisible = useAppSelector(selectIsChatVisible);

  // Track note view start time for performance metrics
  const viewStartTimeRef = useRef<number>();
  const noteViewedTrackedRef = useRef<boolean>(false);

  // Use container hook for integrated data and actions
  const {
    noteData,
    sections,
    messages,
    assistantStatus,
    sendMessage,
    selectOptions,
    handleSectionChange,
    sectionUpdates,
    streamingSections,
    interrupt,
    affectedSectionIds,
    handleApproval,
    submitExpressInterest,
    submitTextResponse,
    isNoteLoading,
    isNoteError,
    noteError,
  } = useNoteChatWithSections(noteId || '');

  const {
    setContext: setToolbarContext,
    show: showToolbar,
    hide: hideToolbar,
    setScrollContainer: setToolbarScrollContainer,
  } = useToolbar();
  const navigate = useNavigate();
  const { note: noteMetrics, performance, error: errorMetrics, noteAudit } = useEventTracking();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const theme = useTheme();

  // Responsive behavior: Auto-hide chat on narrow screens
  const isNarrowScreen = useMediaQuery(theme.breakpoints.down('lg'));

  // Auto-hide chat on narrow screens when note first loads
  // Reset chat visibility to open on wide screens for each new note
  // useLayoutEffect prevents flash by running before browser paint
  useLayoutEffect(() => {
    if (isNarrowScreen) {
      dispatch(setChatVisibility(false));
    } else {
      // Wide screens: Reset chat visibility to open for each new note
      dispatch(setChatVisibility(true));
    }
    // Only run when noteId changes (new note loaded)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteId]);

  // RTK Query mutations and queries
  const [deleteNote] = useDeleteNoteMutation();
  const [updateNoteSection] = useUpdateNoteSectionMutation();
  const [getSignedPdf] = useLazyGetSignedNotePdfQuery();
  const [updateEncounter] = useUpdateEncounterMutation();
  const [createClaim] = useCreateClaimMutation();

  const [isGeneratingNote, setIsGeneratingNote] = useState(false);
  const [isSubmittingClaim, setIsSubmittingClaim] = useState(false);

  // --- SessionBillingPopover state ---
  const [billingPopoverAnchor, setBillingPopoverAnchor] = useState<HTMLElement | null>(null);
  const [billingPopoverSection, setBillingPopoverSection] = useState<PopoverSection | undefined>();
  const [billingPopoverShowSign, setBillingPopoverShowSign] = useState(false);
  const billingPopoverOpen = Boolean(billingPopoverAnchor);

  const handleOpenBillingPopover = useCallback((anchorEl: HTMLElement, section?: PopoverSection, showSign = false) => {
    setBillingPopoverAnchor(anchorEl);
    setBillingPopoverSection(section);
    setBillingPopoverShowSign(showSign);
  }, []);

  const handleCloseBillingPopover = useCallback(() => {
    setBillingPopoverAnchor(null);
    setBillingPopoverSection(undefined);
    setBillingPopoverShowSign(false);
  }, []);

  // ============================================================================
  // NOTE GENERATION V2 STREAMING
  // ============================================================================
  //
  // Derive streaming state from note data OR regeneration query params.
  // Priority:
  // 1. Regeneration info from URL params (for template regeneration flow)
  // 2. Note data thread_id + processing status (for normal note generation)
  // ============================================================================

  // Determine if we should be streaming
  // From regeneration: use query params passed from template switch
  // From note data: check thread_id and processing status
  const shouldStreamFromRegeneration = Boolean(
    regenerationInfo?.isRegenerating && regenerationInfo?.encounterId,
  );
  const shouldStreamFromNoteData = Boolean(
    noteData?.thread_id && noteData?.status === 'processing',
  );
  const shouldStream = shouldStreamFromRegeneration || shouldStreamFromNoteData;

  // Determine which encounter ID to use for streaming
  const streamingEncounterId =
    regenerationInfo?.encounterId ?? noteData?.encounter?.encounter_id ?? undefined;

  // Start/stop streaming based on state
  useEffect(() => {
    setIsGeneratingNote(shouldStream);
  }, [shouldStream]);

  // Clear regeneration info when streaming completes
  const handleStreamingComplete = useCallback(() => {
    dispatch(notesApi.util.invalidateTags([{ type: 'Note', id: noteId }]));
    setIsGeneratingNote(false);
    setRegenerationInfo(null); // Clear regeneration state
  }, [dispatch, noteId]);

  const {
    sections: generationStreamingSections,
    isStreaming: _isGenerationStreaming,
    isComplete: isGenerationComplete,
    workflowStep: generationWorkflowStep,
    completedSteps: generationCompletedSteps,
    workflowNodes: generationWorkflowNodes,
    error: generationStreamingError,
    encounterAnalysis,
  } = useNoteGenerationStreaming({
    encounterId: streamingEncounterId,
    threadId: noteData?.thread_id ?? undefined,
    noteId: noteId || '',
    onComplete: handleStreamingComplete,
    skip: !shouldStream,
  });

  // Show snackbar when streaming error occurs
  useEffect(() => {
    if (generationStreamingError) {
      enqueueSnackbar(generationStreamingError, { variant: 'error' });
    }
  }, [generationStreamingError, enqueueSnackbar]);

  // Invalidate note cache when "populate_sections" completes to fetch real sections from DB
  const populateSectionsCompletedRef = useRef(false);
  useEffect(() => {
    const isPopulateDone = generationCompletedSteps.includes('populate_sections');
    // Only invalidate once when populate_sections first completes
    if (isPopulateDone && !populateSectionsCompletedRef.current && noteId) {
      populateSectionsCompletedRef.current = true;
      dispatch(notesApi.util.invalidateTags([{ type: 'Note', id: noteId }]));
    }
    // Reset ref when noteId changes (new note)
    if (!isPopulateDone) {
      populateSectionsCompletedRef.current = false;
    }
  }, [generationCompletedSteps, noteId, dispatch]);

  // ============================================================================
  // END NOTE GENERATION V2 STREAMING
  // ============================================================================

  // Determine if we should show the generation view (progress + streaming sections)
  // - Show immediately when note is in processing state with thread_id (even before stream starts)
  // - Hide when streaming is complete AND real sections have loaded
  // - Hide when "populate_sections" step completes (sections saved to DB, switch to real sections for editing)
  const hasRealSections = sections && sections.length > 0;
  const hasStreamingData = generationStreamingSections && generationStreamingSections.length > 0;
  const isPopulateSectionsCompleted = generationCompletedSteps.includes('populate_sections');
  const showGenerationView =
    shouldStream &&
    !isPopulateSectionsCompleted &&
    !(isGenerationComplete && hasRealSections) &&
    (!hasRealSections || hasStreamingData);

  /**
   * Feature flags and patient context
   */
  const hasNoteSigning = useFeatureFlag('note_signing');
  const hasBillingFeature = useFeatureGate('jotbill');
  const isJotCoachEnabled = useFeatureFlag('jotcoach_billing_analysis');
  const isAuditEnabled = useAuditEnabled();
  const hasPatient = Boolean(noteData?.patient_id);
  const hasTranscript = Boolean(noteData?.transcript && noteData.transcript.length > 0);
  const encounterId = noteData?.encounter?.encounter_id;
  const { update: updateBillingClaim, flush: flushBillingClaim } = useDebouncedWorkingBillingClaim({ encounterId });
  const { userState } = useUser();

  // Sidebar mode from Redux (for payer template feature)
  const sidebarMode = useAppSelector(selectAuditSidebarMode);

  // Chat onboarding overlay state from Redux
  const isChatOnboardingVisible = useAppSelector(selectIsChatOnboardingVisible);

  // Chat onboarding hooks - check if user has completed onboarding
  const isAiChatOnboarded = useOnboardingStep('note_ai_chat_onboarding_completed');
  const updateOnboardingStep = useUpdateOnboardingStep();

  // ============================================================================
  // ENCOUNTER & JOTCOACH DATA FETCHING
  // ============================================================================
  //
  // NOTE: This section fetches encounter and JotCoach data directly via RTK Query.
  // This is a temporary pattern until we can refactor the data layer.
  //
  // Why we need this:
  // - noteData.encounter.outputs may be stale or incomplete
  // - Direct RTK Query fetch ensures fresh encounter outputs for tabs (other notes, jotdocs)
  // - JotCoach encounter session provides compliance data for future audit features
  //
  // Pattern borrowed from: src/web-app/src/features/notes/routes/NoteJotDocView.tsx
  // ============================================================================

  /**
   * Fetch encounter data directly using RTK Query
   * Provides complete and up-to-date encounter outputs for tab navigation
   */
  const { data: encounterData, isLoading: encounterLoading } = useGetEncounterQuery(
    { encounterId: encounterId! },
    { skip: !encounterId },
  );

  // Check if encounter analysis is available (must be after encounterData is fetched)
  // encounter_analysis is a new field on the encounter response, cast to access it
  const hasEncounterAnalysis = Boolean((encounterData as any)?.encounter_analysis);
  const encounterAnalysisData = (encounterData as any)?.encounter_analysis as Record<string, any> | undefined;
  
  // Feature gate for encounter analysis sidebar
  const isEncounterAnalysisSidebarEnabled = useFeatureGate('feature_encounter_analysis_sidebar');

  /**
   * Fetch JotCoach encounter session (lazy query for manual control)
   * Used for accessing compliance data and other JotCoach features
   */
  const { fetchSession: fetchEncounterJotcoachSession, encounterSession } =
    useJotCoachEncounterSessionLazy(encounterId);

  // Extract note-specific session from encounter session
  // Note: Session keys may be prefixed with "note:" (e.g., "note:uuid") or just be the uuid
  const noteJotcoachSession = useMemo(() => {
    if (!encounterSession?.sessions || !noteId) {
      return null;
    }

    // Try both with and without "note:" prefix
    const sessionKey = `note:${noteId}`;
    const session =
      encounterSession.sessions[noteId] || encounterSession.sessions[sessionKey] || null;

    return session;
  }, [encounterSession, noteId]);

  // Extract documentation analysis from note session
  // Note: The structure has documentation_analysis.analysis_data, not rules_analysis
  const documentationAnalysis = useMemo(() => {
    // Check for documentation_analysis.analysis_data (new structure) or rules_analysis (old structure)
    const analysisData =
      noteJotcoachSession?.documentation_analysis?.analysis_data ||
      noteJotcoachSession?.rules_analysis ||
      null;
    return analysisData;
  }, [noteJotcoachSession]);

  // ============================================================================
  // END ENCOUNTER & JOTCOACH DATA FETCHING
  // ============================================================================

  // ============================================================================
  // NOTE AUDIT (UNIFIED HOOK)
  // ============================================================================
  //
  // Unified hook that handles:
  // - Initial data fetching via RTK Query
  // - Optional polling when data not yet available
  // - Reaudit triggering with SSE streaming for real-time progress
  // - Derived values (issueCount, sectionIssueMap, cptAnalysis)
  // ============================================================================

  const audit = useNoteAudit(isAuditEnabled ? noteId : undefined, {
    enabled: isAuditEnabled,
    polling: {
      initial: { interval: 10_000, maxDuration: 180_000 },
    },
    encounterId: encounterId ?? undefined,
  });

  // Destructure commonly used values for convenience
  const { issueCount: totalIssueCount, sectionIssueMap, data: noteAuditData } = audit;

  // Reset audit UI state when unmounting
  useEffect(() => {
    return () => {
      dispatch(resetAuditUi());
    };
  }, [dispatch]);

  // Track when audit sidebar is viewed (mode switches to audit with data available)
  const auditSidebarViewedRef = useRef<string | null>(null);
  useEffect(() => {
    if (sidebarMode === 'audit' && noteAuditData && noteId) {
      // Only track once per note
      if (auditSidebarViewedRef.current !== noteId) {
        auditSidebarViewedRef.current = noteId;

        // Count rules by status
        const rules = noteAuditData.rule_compliance_assessments ?? [];
        const nonCompliantCount = rules.filter(r => r.compliant === 'no').length;
        const warningCount = rules.filter(r => r.compliant === 'warning').length;
        const compliantCount = rules.filter(r => r.compliant === 'yes').length;
        const issueCount = nonCompliantCount + warningCount;

        noteAudit.sidebarViewed({
          note_id: noteId,
          encounter_id: encounterId,
          issue_count: issueCount,
          non_compliant_count: nonCompliantCount,
          warning_count: warningCount,
          compliant_count: compliantCount,
        });
      }
    }
  }, [sidebarMode, noteAuditData, noteId, encounterId, noteAudit]);

  // Show toast notification when audit data first arrives
  const auditToastShownRef = useRef<string | null>(null);
  const auditSnackbarKeyRef = useRef<string | number | null>(null);
  useEffect(() => {
    if (isAuditEnabled && noteAuditData && noteId && audit.state === 'ready') {
      // Only show toast once per note
      if (auditToastShownRef.current !== noteId) {
        auditToastShownRef.current = noteId;

        // Use the same issue count as the badge (non-compliant only)
        const issueCount = totalIssueCount;

        // Show toast with audit icon
        const snackbarKey = enqueueSnackbar(
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
            }}
            onClick={() => {
              // Track sidebar opened from snackbar
              noteAudit.sidebarOpened({
                note_id: noteId,
                encounter_id: encounterId,
                source: 'snackbar',
                issue_count: issueCount,
              });
              dispatch(setSidebarMode('audit'));
              // Close the snackbar when clicked
              if (auditSnackbarKeyRef.current !== null) {
                closeSnackbar(auditSnackbarKeyRef.current);
              }
            }}
          >
            <AuditIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Note Audit Complete
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {issueCount > 0
                  ? `${issueCount} issue${issueCount === 1 ? '' : 's'} found • Click to view`
                  : 'All rules compliant • Click to view'}
              </Typography>
            </Box>
          </Box>,
          {
            variant: issueCount > 0 ? 'info' : 'success',
            autoHideDuration: 5000,
          },
        );
        // Store the snackbar key so we can close it later
        auditSnackbarKeyRef.current = snackbarKey;
      }
    }
  }, [
    isAuditEnabled,
    noteAuditData,
    noteId,
    audit.state,
    totalIssueCount,
    enqueueSnackbar,
    closeSnackbar,
    dispatch,
  ]);

  // ============================================================================
  // END NOTE AUDIT
  // ============================================================================

  // View mode management hook
  const { viewMode, isTranscriptMode, enterTranscriptMode, exitTranscriptMode } = useNoteViewMode({
    noteId: noteId || '',
    encounterId: encounterId ?? undefined,
    completionStatus: noteData?.completion_status ?? undefined,
  });

  // Track encounter analysis mode separately (similar to transcript mode)
  const [isEncounterAnalysisMode, setIsEncounterAnalysisMode] = useState(false);

  const enterEncounterAnalysisMode = useCallback(() => {
    setIsEncounterAnalysisMode(true);
  }, []);

  const exitEncounterAnalysisMode = useCallback(() => {
    setIsEncounterAnalysisMode(false);
  }, []);

  // CSAT trigger hook
  const { triggerCSAT } = useCSATTrigger();

  // Support links for CSAT modal (shown when users rate 1 or 2)
  const CSAT_SUPPORT_LINKS = [
    {
      label: 'Report an issue',
      href: 'mailto:support@jotpsych.com',
    },
    {
      label: 'Contact us',
      href: 'mailto:contact@jotpsych.com',
    },
    {
      label: 'Chat support',
      onClick: () => dispatch(openChatWindow()),
    },
  ];

  // Track previous completion status to detect changes
  const previousCompletionStatusRef = useRef<string | null | undefined>();

  // Initialize ref on mount
  useEffect(() => {
    if (previousCompletionStatusRef.current === undefined) {
      previousCompletionStatusRef.current = noteData?.completion_status ?? null;
    }
  }, []);

  // Trigger CSAT when note is completed or signed
  useEffect(() => {
    const currentStatus = noteData?.completion_status ?? null;
    const previousStatus = previousCompletionStatusRef.current;

    // Check if status changed from non-'completed' to 'completed'
    if (
      currentStatus === 'completed' &&
      previousStatus !== 'completed' &&
      previousStatus !== undefined &&
      previousStatus !== null &&
      noteId
    ) {
      triggerCSAT({
        context: 'note',
        contextId: noteId,
        supportLinks: CSAT_SUPPORT_LINKS,
      });
    }

    // Update ref for next comparison
    previousCompletionStatusRef.current = currentStatus;
  }, [noteData?.completion_status, noteId, triggerCSAT]);

  // Note signing workflow hook
  const {
    isOnboardingOpen,
    setIsOnboardingOpen,
    isUpdatingNote,
    handleSignNote,
    handleAmendNote,
    handleOnboardingComplete,
  } = useNoteSigning({
    noteId: noteId || '',
    encounterId: encounterId ?? undefined,
    hasPatient,
    hasNoteSigning,
    isSigned: noteData?.is_signed ?? undefined,
  });

  /**
   * Calculate if note components should be editable
   * Editable in edit mode (no longer blocking on interrupts for note_changes)
   * Also blocked when note generation is streaming (v2 flow)
   * BUT allowed once "populate_sections" step completes (sections saved to DB, even if post-processing continues)
   */
  const isEditable = viewMode === 'edit' && (!isGeneratingNote || isPopulateSectionsCompleted);

  // Get previous content for diff modal
  const previousContent = useAppSelector(selectSectionPreviousContent);

  // Handler for approving AI suggestion (dismisses affected state)
  const handleApprove = useCallback(() => {
    // No-op - clearing is handled in NoteSectionItem
  }, []);

  // Handler for reverting AI suggestion
  const handleRevert = useCallback(() => {
    // No-op - reverting is handled in NoteSectionItem
  }, []);

  // Handle clear chat from sidebar container
  const handleClearChat = useCallback(() => {
    if (noteId) {
      dispatch(initializeChat({ noteId }));
    }
  }, [dispatch, noteId]);

  // Handle scroll to section
  // sectionIdOrName can be either a section_id (UUID) or a section name (like "HPI", "Assessment", etc.)
  const handleScrollToSection = useCallback(
    (sectionIdOrName: string) => {
      // First, try to find by section_id (exact match)
      let element = document.querySelector(`[data-section-id="${sectionIdOrName}"]`);

      // If not found, try to find by section name (normalize and match)
      if (!element && sections.length > 0) {
        // Normalize the input (lowercase, replace spaces/special chars with dashes)
        const normalize = (str: string) =>
          str
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');

        const normalizedInput = normalize(sectionIdOrName);

        // Find matching section by name
        const matchingSection = sections.find(section => {
          const normalizedName = normalize(section.name);
          return (
            normalizedName === normalizedInput ||
            section.name.toLowerCase() === sectionIdOrName.toLowerCase()
          );
        });

        if (matchingSection) {
          element = document.querySelector(`[data-section-id="${matchingSection.section_id}"]`);
        }
      }

      if (element) {
        // Get the scrollable container
        const container = document.querySelector(LAYOUT_SELECTORS.NOTE_SECTIONS_CONTAINER);

        if (container) {
          // Calculate scroll position relative to container
          const containerRect = container.getBoundingClientRect();
          const elementRect = element.getBoundingClientRect();
          const scrollTop = container.scrollTop;
          const offset = elementRect.top - containerRect.top + scrollTop;

          // Smooth scroll within container (using SCROLL_OFFSET from layout constants)
          container.scrollTo({
            top: offset - 170, // LAYOUT_DIMENSIONS.SCROLL_OFFSET
            behavior: 'smooth',
          });
        } else {
          // Fallback to window scroll
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }
    },
    [sections],
  );

  // Handle fix with AI - switches to chat mode and sends a fix message
  const handleFixWithAI = useCallback(
    (reason: string) => {
      // Switch to chat mode so user can see the conversation
      dispatch(setSidebarMode('chat'));
      // Send the fix message to the AI
      sendMessage(`Fix the following issue: ${reason}`);
    },
    [dispatch, sendMessage],
  );

  // Handle using suggested CPT codes from audit sidebar or metadata bar
  const handleUseSuggestedCodes = useCallback(
    async (codes: string[], source: 'audit_sidebar' | 'metadata_bar' = 'audit_sidebar') => {
      if (!encounterId || codes.length === 0) return;

      // Clean CPT codes: strip non-numeric characters, limit to 5 digits, filter empty
      const { cleanCptCodes } = await import('@components/ui/forms/CPTCodePicker');
      const cleanedCodes = cleanCptCodes(codes);

      if (cleanedCodes.length === 0) return;

      // Mark suggested codes as ai_recommended
      const lineItems = cleanedCodes.map(code => ({
        code_id: `CPT:${code}`,
        source: 'ai_recommended' as const,
      }));

      updateBillingClaim({ lineItems });

      enqueueSnackbar('CPT codes updated to suggested values', { variant: 'success' });

      // Track analytics event
      noteAudit.suggestedCodesApplied?.({
        note_id: noteId,
        encounter_id: encounterId,
        codes_applied: cleanedCodes,
        source,
      });

      // Trigger reaudit for metadata_bar source (sidebar already triggers it internally)
      if (source === 'metadata_bar') {
        // Calculate current issue count for tracking
        const currentIssueCount =
          noteAuditData?.rule_compliance_assessments?.filter(
            r => r.compliant === 'no' || r.compliant === 'warning',
          ).length ?? 0;

        // Track that the reaudit was triggered by applying suggested codes
        noteAudit.runClicked({
          note_id: noteId,
          encounter_id: encounterId,
          current_issue_count: currentIssueCount,
          source: 'suggested_codes_applied',
        });

        // Flush the debounced update, then trigger reaudit
        await flushBillingClaim();
        setTimeout(() => {
          void audit.triggerReaudit();
        }, 500);
      }
    },
    [encounterId, updateBillingClaim, flushBillingClaim, enqueueSnackbar, noteId, noteAudit, audit, noteAuditData],
  );

  /**
   * Handle viewing last encounter note from patient context
   */
  const handleViewLastEncounterNote = useCallback(
    (lastEncounterNoteId: string) => {
      if (lastEncounterNoteId) {
        navigate(`/note/${lastEncounterNoteId}`);
      }
    },
    [navigate],
  );

  /**
   * Handle CPT codes changed in metadata bar
   * Automatically triggers reaudit when CPT codes are updated
   */
  const handleCptCodesChanged = useCallback(
    (_codes: string[]) => {
      if (!noteId || !isAuditEnabled) return;

      // Calculate current issue count for tracking
      const currentIssueCount =
        noteAuditData?.rule_compliance_assessments?.filter(
          r => r.compliant === 'no' || r.compliant === 'warning',
        ).length ?? 0;

      // Track that the reaudit was triggered by CPT code change
      noteAudit.runClicked({
        note_id: noteId,
        encounter_id: encounterId,
        current_issue_count: currentIssueCount,
        source: 'cpt_code_change',
      });

      // Wait a moment for the encounter update to propagate, then trigger reaudit
      setTimeout(() => {
        void audit.triggerReaudit();
      }, 500);
    },
    [noteId, isAuditEnabled, noteAuditData, noteAudit, encounterId, audit],
  );

  // --- SessionBillingPopover data callbacks ---

  const handleBillingModalityChange = useCallback(
    async (newModality: Modality) => {
      if (!encounterId) return;
      const pos = newModality === 'in_person' ? '11' : newModality === 'video' ? '10' : '02';
      await updateEncounter({
        encounterId,
        encounterUpdate: { modality: newModality },
      });
      updateBillingClaim({ placeOfService: pos });
    },
    [encounterId, updateEncounter, updateBillingClaim],
  );

  const handleBillingPlaceOfServiceChange = useCallback(
    (pos: string) => {
      updateBillingClaim({ placeOfService: pos });
    },
    [updateBillingClaim],
  );

  const handleBillingDurationChange = useCallback(
    async (minutes: number) => {
      if (!encounterId) return;
      // Calculate new end_time from start_time + duration
      const startTime = encounterData?.start_time;
      if (startTime) {
        const endTime = new Date(new Date(startTime).getTime() + minutes * 60 * 1000).toISOString();
        await updateEncounter({
          encounterId,
          encounterUpdate: { end_time: endTime },
        });
      }
    },
    [encounterId, encounterData?.start_time, updateEncounter],
  );

  const handleBillingCptCodesChange = useCallback(
    (codes: string[]) => {
      const claim = encounterData?.working_billing_claim;
      updateBillingClaim({
        lineItems: bareCodesToLineItems(codes, claim?.line_items ?? undefined),
      });
      handleCptCodesChanged(codes);
    },
    [encounterData?.working_billing_claim, updateBillingClaim, handleCptCodesChanged],
  );

  const handleBillingIcd10CodesChange = useCallback(
    (codes: string[]) => {
      const filtered = codes.filter(c => c && c.trim() !== '');
      const claim = encounterData?.working_billing_claim;
      updateBillingClaim({
        diagnosisCodes: filtered,
        diagnosisCodeSources: buildDiagnosisCodeSources(
          filtered,
          claim?.diagnosis_code_sources,
        ),
      });
    },
    [encounterData?.working_billing_claim, updateBillingClaim],
  );

  const handleBillingAttestationChange = useCallback(
    async (field: keyof EncounterAttestations, value: boolean | number | null) => {
      if (!encounterId) return;
      const currentAttestations = (encounterData?.attestations ?? {}) as EncounterAttestations;
      await updateEncounter({
        encounterId,
        encounterUpdate: {
          attestations: { ...currentAttestations, [field]: value },
        },
      });
    },
    [encounterId, encounterData?.attestations, updateEncounter],
  );

  const handleBillingTimeChange = useCallback(
    async (updates: { startTime?: string; endTime?: string }) => {
      if (!encounterId) return;
      const encounterUpdate: Record<string, unknown> = {};
      if (updates.startTime !== undefined) encounterUpdate.start_time = updates.startTime;
      if (updates.endTime !== undefined) encounterUpdate.end_time = updates.endTime;
      await updateEncounter({
        encounterId,
        encounterUpdate,
      });
    },
    [encounterId, updateEncounter],
  );

  /**
   * Handle claim creation
   */
  const handleCreateClaim = useCallback(async () => {
    if (!noteData?.patient_id || !encounterId || isSubmittingClaim) return;

    setIsSubmittingClaim(true);
    try {
      await createClaim({
        createClaimRequest: {
          encounter_ids: [encounterId],
          patient_id: noteData.patient_id,
        },
      }).unwrap();

      // Show success message and stay on the page
      enqueueSnackbar('Claim created successfully', { variant: 'success' });
    } catch (error) {
      console.error('Failed to create claim:', error);
      const errorMessage =
        error && typeof error === 'object' && 'message' in error
          ? (error as Error).message
          : 'Failed to create claim';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setIsSubmittingClaim(false);
    }
  }, [noteData?.patient_id, encounterId, isSubmittingClaim, createClaim, enqueueSnackbar]);

  /**
   * Calculate chat visibility for desktop sidebar
   * Desktop sidebar is visible when: in edit mode AND not in transcript/analysis mode AND Redux visibility state is true AND not on narrow screen
   * On narrow screens, the MobileChatDrawer is used instead
   */
  const chatVisible = viewMode === 'edit' && !isTranscriptMode && !isEncounterAnalysisMode && isChatVisible && !isNarrowScreen;

  /**
   * Calculate mobile drawer visibility
   * Mobile drawer is available on narrow screens in edit mode (not transcript/analysis mode)
   */
  const showMobileDrawer = viewMode === 'edit' && !isTranscriptMode && !isEncounterAnalysisMode && isNarrowScreen;

  // Store BlockNote editor registry from NoteSectionsList for copy operations
  const blockNoteEditorsRef = useRef<Map<string, AnyBlockNoteEditor>>(new Map());

  // Callback to receive editor registry from NoteSectionsList
  const handleEditorsReady = useCallback((editorsMap: Map<string, AnyBlockNoteEditor>) => {
    blockNoteEditorsRef.current = editorsMap;
  }, []);

  // Copy functionality hook - uses encounter data for header with fallbacks
  const {
    copyAllSections: originalCopyAllSections,
    copySelectedSections: originalCopySelectedSections,
  } = useNoteCopy({
    noteId: noteId || '',
    noteData,
    encounterData,
    selectedSectionIds,
    dirtySections,
    blockNoteEditors: blockNoteEditorsRef.current,
  });

  // Wrapper functions to trigger CSAT after successful copy
  const copyAllSections = useCallback(async () => {
    await originalCopyAllSections();
    // Trigger CSAT after successful copy
    if (noteId) {
      triggerCSAT({
        context: 'note',
        contextId: noteId,
        supportLinks: CSAT_SUPPORT_LINKS,
      });
    }
  }, [originalCopyAllSections, noteId, triggerCSAT]);

  const copySelectedSections = useCallback(async () => {
    await originalCopySelectedSections();
    // Trigger CSAT after successful copy
    if (noteId) {
      triggerCSAT({
        context: 'note',
        contextId: noteId,
        supportLinks: CSAT_SUPPORT_LINKS,
      });
    }
  }, [originalCopySelectedSections, noteId, triggerCSAT]);

  // Approval navigation hook for affected sections
  const approvalNavigation = useApprovalNavigation(
    affectedSectionIds,
    LAYOUT_SELECTORS.NOTE_SECTIONS_CONTAINER,
  );

  // Navigation guard to prevent losing unsaved edits
  const { onSaveStart, markDirty } = useNoteSectionSaveGuard();

  // Invalidate note cache and clear dirty sections when leaving the view or switching notes
  useEffect(() => {
    return () => {
      if (noteId) {
        dispatch(notesApi.util.invalidateTags([{ type: 'Note', id: noteId }]));
      }
      // Clear dirty sections when navigating away or switching notes
      dispatch(clearAllDirtySections());
    };
  }, [noteId, dispatch]);

  /**
   * Extract current note title from encounter outputs for consistency
   * This ensures the current note tab shows the same title as it would if it were in otherNotes
   */
  const currentNoteTitle = useMemo(() => {
    if (!encounterData?.outputs || !noteId) {
      return noteData?.note_title;
    }

    const currentOutput = encounterData.outputs.find(
      output =>
        output.type?.toLowerCase() === 'note' &&
        'encounter_document_id' in output &&
        output.encounter_document_id === noteId,
    );

    // Use encounter output title if available, fallback to noteData.note_title
    return currentOutput?.title || noteData?.note_title;
  }, [encounterData?.outputs, noteId, noteData?.note_title]);

  /**
   * Extract ALL notes from encounter outputs for tab navigation
   * Includes the current note to maintain stable tab order
   */
  const allEncounterNotes = useMemo(() => {
    if (!encounterData?.outputs) return [];

    return encounterData.outputs
      .filter(output => {
        // Type guard: check if output has encounter_document_id property
        return (
          output.type?.toLowerCase() === 'note' &&
          'encounter_document_id' in output &&
          output.encounter_document_id !== undefined
        );
      })
      .map(output => {
        const noteOutput = output as {
          encounter_document_id: string;
          title?: string;
          created_at?: string | null;
          status?: 'pending' | 'completed';
          task_id?: string | null;
        };
        const isPending = noteOutput.status === 'pending';
        return {
          encounter_document_id: noteOutput.encounter_document_id,
          title: noteOutput.title,
          created_at: noteOutput.created_at ?? undefined,
          isPending,
        };
      });
  }, [encounterData?.outputs]);

  /**
   * For backwards compatibility, also provide otherNotes (excluding current)
   * Used in some UI components that need to know about other notes
   */
  const otherNotes = useMemo(() => {
    return allEncounterNotes.filter(note => note.encounter_document_id !== noteId);
  }, [allEncounterNotes, noteId]);

  /**
   * Extract jotdocs from encounter outputs for tab navigation
   */
  const jotdocs = useMemo(() => {
    return (
      encounterData?.outputs?.filter(
        (output): output is any => 'jotdoc_id' in output && Boolean(output.jotdoc_id),
      ) ?? []
    );
  }, [encounterData?.outputs]);

  /**
   * Determine active tab value based on whether we have multiple notes
   * This hook MUST be called before any early returns to maintain hook order
   */
  const activeTabValue = useMemo(() => {
    if (isTranscriptMode) return 'transcript';
    if (isEncounterAnalysisMode) return 'encounter-analysis';

    // If there are multiple notes in the encounter, use note-{id} format for all
    if (noteId && allEncounterNotes.length > 1) {
      return `note-${noteId}`;
    }

    // Legacy single note mode
    return 'note';
  }, [isTranscriptMode, isEncounterAnalysisMode, noteId, allEncounterNotes.length]);

  // Modal system for Find & Replace
  const { open: openModal } = useModal();

  // Redux state for Find & Replace
  const findReplaceAction = useAppSelector(state => state.findReplace.action);

  /**
   * Handle note deletion using RTK Query mutation
   */
  const handleDeleteNote = async () => {
    if (!noteId) return;

    try {
      const result = await deleteNote({ noteId }).unwrap();
      if (result.status === 200) {
        navigate('/notes');
      }
    } catch (error) {
      console.error('Failed to delete note:', error);
    }
  };

  /**
   * Handle Find & Replace - opens modal with current sections
   */
  const handleFindReplace = () => {
    if (!noteId) return;

    openModal(MODAL_TYPES.FIX_SPELLING, {
      sections,
      analyticsMetadata: {
        note_id: noteId,
        source: 'note-top-bar',
        prefilled_term: false,
        encounter_id: '', // Not available in this context
      },
    });
  };

  /**
   * Handle Copy/Paste Settings - opens modal
   */
  const handleCopyPasteSettings = () => {
    openModal(MODAL_TYPES.COPY_PASTE_PREFERENCES, {});
  };

  /**
   * Handle downloading signed note PDF
   */
  const handleDownloadSignedPdf = async () => {
    if (!noteId) return;

    try {
      const blob = await getSignedPdf({ noteId }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `signed-note-${noteId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to download signed note';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      errorMetrics.track(errorMessage, 'download_signed_pdf');
    }
  };

  /**
   * Handle tab change from MUI Tabs component
   * Routes to view switching handlers or navigates to other notes/jotdocs
   */
  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    if (newValue === 'transcript') {
      // Track transcript tab click (Phase 4: Navigation/tab events)
      if (noteId) {
        noteMetrics.tabsTranscriptClicked?.(noteId, {
          previous_tab: isTranscriptMode ? 'transcript' : isEncounterAnalysisMode ? 'encounter-analysis' : 'note',
          encounter_id: encounterId,
        });
      }
      exitEncounterAnalysisMode();
      enterTranscriptMode();
    } else if (newValue === 'encounter-analysis') {
      // Exit other modes and enter encounter analysis mode
      exitTranscriptMode();
      enterEncounterAnalysisMode();
      
      // Track encounter analysis tab click
      if (noteId) {
        noteMetrics.tabsNoteClicked?.(noteId, {
          previous_tab: isTranscriptMode ? 'transcript' : isEncounterAnalysisMode ? 'encounter-analysis' : 'note',
          source: 'encounter-analysis',
          encounter_id: encounterId,
        });
      }
    } else if (newValue === 'note') {
      // Track note tab click (Phase 4: Navigation/tab events)
      if (noteId) {
        noteMetrics.tabsNoteClicked?.(noteId, {
          previous_tab: isTranscriptMode ? 'transcript' : isEncounterAnalysisMode ? 'encounter-analysis' : 'note',
          encounter_id: encounterId,
        });
      }
      exitTranscriptMode();
      exitEncounterAnalysisMode();
    } else if (newValue.startsWith('note-')) {
      // Navigate to another note from the same encounter
      const otherNoteId = newValue.replace('note-', '');
      const targetNote = otherNotes.find(n => n.encounter_document_id === otherNoteId);

      // Block navigation if note is still pending
      if (targetNote?.isPending) {
        enqueueSnackbar('This will be ready in a few more seconds', { variant: 'info' });
        return;
      }

      // Track other note tab click (Phase 4: Navigation/tab events)
      if (noteId) {
        noteMetrics.tabsNoteClicked?.(otherNoteId, {
          note_id: noteId,
          encounter_id: encounterId,
          source: 'note-tabs',
          previous_tab: isTranscriptMode ? 'transcript' : 'note',
        });
      }

      // Exit transcript/analysis mode before navigating to ensure new note opens in note view
      if (isTranscriptMode) {
        exitTranscriptMode();
      }
      if (isEncounterAnalysisMode) {
        exitEncounterAnalysisMode();
      }

      // Navigate to the other note
      navigate(`/note/${otherNoteId}`, {
        state: { encounterId },
      });
    } else if (newValue.startsWith('jotdoc-')) {
      const jotdocId = newValue.replace('jotdoc-', '');

      // Track jotdoc tab click (Phase 4: Navigation/tab events)
      if (noteId) {
        noteMetrics.tabsJotdocClicked?.(jotdocId, {
          note_id: noteId,
          encounter_id: encounterId,
          source: 'note-tabs',
        });
      }

      // Track jotdoc navigation (existing)
      if (noteId) {
        noteMetrics.viewed(jotdocId, {
          source: 'note-tabs',
        });
      }

      // Exit transcript/analysis mode before navigating
      if (isTranscriptMode) {
        exitTranscriptMode();
      }
      if (isEncounterAnalysisMode) {
        exitEncounterAnalysisMode();
      }

      navigate(`/jotdoc/${jotdocId}`, {
        state: { noteId, encounterId },
      });
    }
  };

  // Show toolbar and update context with patient ID and note ID
  useEffect(() => {
    const timer = setTimeout(() => {
      setToolbarScrollContainer(LAYOUT_SELECTORS.NOTE_SECTIONS_CONTAINER);
    }, 10);

    showToolbar();
    setToolbarContext({
      patientId: noteData?.patient_id ?? undefined,
      noteId: noteId,
      source: 'note',
    });

    // Cleanup: Hide toolbar and clear context when component unmounts
    return () => {
      clearTimeout(timer);
      hideToolbar();
      setToolbarContext(undefined);
      setToolbarScrollContainer(undefined);
    };
  }, [
    noteData?.patient_id,
    noteId,
    setToolbarContext,
    showToolbar,
    hideToolbar,
    setToolbarScrollContainer,
  ]);

  // Hide toolbar when note fails to load (error state)
  useEffect(() => {
    if (isNoteError) {
      hideToolbar();
    }
  }, [isNoteError, hideToolbar]);

  // Initialize chat on mount
  useEffect(() => {
    if (noteId && (!currentNoteId || currentNoteId !== noteId)) {
      dispatch(initializeChat({ noteId }));
    }
  }, [dispatch, noteId, currentNoteId]);

  // Track SSE performance metrics for streaming responses
  useSSEPerformanceTracking(noteId || '');

  // Note: Chat visibility is now controlled by user action on all screen sizes
  // On narrow screens, the MobileChatDrawer is used instead of the sidebar

  // Show chat onboarding overlay when chat becomes visible (only once per user)
  useEffect(() => {
    if (chatVisible && !isNarrowScreen && !isAiChatOnboarded && noteId) {
      dispatch(setChatOnboardingVisibility(true));

      // Track overlay shown event
      noteMetrics.aiChatOnboardingShown(noteId, {
        encounter_id: encounterId,
      });
    }
  }, [dispatch, chatVisible, isNarrowScreen, isAiChatOnboarded, noteId, noteMetrics, encounterId]);

  // Apply find/replace when action is dispatched from modal
  useEffect(() => {
    if (!findReplaceAction || !noteId) return;

    const applyFindReplace = async () => {
      const { find, replace, caseSensitive } = findReplaceAction;

      // Apply find/replace to sections
      const { updatedSections } = findReplaceInSections(sections, find, replace, caseSensitive);

      // Update each modified section via RTK Query
      for (let i = 0; i < updatedSections.length; i++) {
        const section = updatedSections[i];
        if (section.edited_text !== sections[i]?.edited_text) {
          try {
            await updateNoteSection({
              noteId,
              sectionIndex: i,
              editSectionRequest: {
                edited_text: section.edited_text || '',
              },
            }).unwrap();
          } catch (error) {
            console.error(`Failed to update section ${i}:`, error);
          }
        }
      }

      // Clear the Redux action after applying
      dispatch(clearFindReplace());
    };

    applyFindReplace();

    // sections intentionally excluded from deps - we want current sections when action fires, not re-run on every section change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findReplaceAction, noteId, updateNoteSection, dispatch]);

  // Register encounter_id with telemetry service when available
  useEffect(() => {
    if (encounterId) {
      NoteTelemetryService.setEncounterId(encounterId);
    }
  }, [encounterId]);

  // ============================================================================
  // JOTCOACH ENCOUNTER SESSION EFFECTS
  // ============================================================================
  //
  // These effects manage JotCoach encounter session fetching and polling.
  // Pattern borrowed from: src/web-app/src/features/notes/routes/NoteJotDocView.tsx
  //
  // Flow:
  // 1. Initial fetch when encounterId becomes available
  // 2. Poll every 10 seconds until workflow completes (has compliance + attestation data)
  // 3. Auto-stop after 3 minutes as safety timeout
  // ============================================================================

  // Effect 1: Initial fetch of JotCoach encounter session
  useEffect(() => {
    // Only fetch if JotCoach feature flag is enabled
    if (encounterId && isJotCoachEnabled) {
      fetchEncounterJotcoachSession();
    }
  }, [encounterId, isJotCoachEnabled, fetchEncounterJotcoachSession]);

  // Effect 2: Poll for JotCoach encounter session until workflow completes
  useEffect(() => {
    // Don't poll if JotCoach feature flag is disabled or missing required IDs
    if (!encounterId || !noteId || !isJotCoachEnabled) {
      return;
    }

    // Check if workflow has completed for this note
    const noteKey = `note:${noteId}`;
    const docSession = encounterSession?.sessions?.[noteKey];
    const hasRuleComplianceAssessments = docSession?.rule_compliance_assessments !== undefined;
    const hasAttestationEdits = docSession?.attestation_edits !== undefined;

    // Stop polling if workflow is complete
    if (hasRuleComplianceAssessments && hasAttestationEdits) {
      return;
    }

    // Poll every 10 seconds
    const intervalId = window.setInterval(() => {
      fetchEncounterJotcoachSession();
    }, 10000);

    // Safety timeout: stop polling after 3 minutes
    const timeoutId = window.setTimeout(
      () => {
        clearInterval(intervalId);
      },
      3 * 60 * 1000,
    );

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [
    encounterId,
    noteId,
    isJotCoachEnabled,
    encounterSession?.sessions,
    fetchEncounterJotcoachSession,
  ]);

  // ============================================================================
  // END JOTCOACH ENCOUNTER SESSION EFFECTS
  // ============================================================================

  // Track note viewed when note loads (Phase 1: Core view tracking)
  useEffect(() => {
    if (!noteId || !noteData || isNoteLoading || noteViewedTrackedRef.current) {
      return;
    }

    // Mark as tracked to prevent duplicate events
    noteViewedTrackedRef.current = true;

    // Start performance timer
    viewStartTimeRef.current = performance.now();

    // Track note viewed (Statsig)
    noteMetrics.viewed(noteId, {
      source: 'direct',
    });

    // Track note viewed (Telemetry - dual tracking)
    trackNoteTelemetry('note_viewed', {
      docId: `note:${noteId}`,
      docVersion: '0',
      metadata: {
        note_id: noteId,
        encounter_id: encounterId,
      },
    });
  }, [noteId, noteData, isNoteLoading, noteMetrics, performance, encounterId]);

  // Track performance timing on unmount (Phase 1: Core view tracking)
  useEffect(() => {
    return () => {
      if (noteId && viewStartTimeRef.current) {
        const duration = performance.now() - viewStartTimeRef.current;

        // Track time spent on note
        performance.timing(`note_view_${noteId}`, duration, {
          note_id: noteId,
          encounter_id: encounterId,
        });
      }
    };
  }, [noteId, performance, encounterId]);

  // Handle sidebar mode change - must be defined before early return
  const handleSidebarModeChange = useCallback(
    (newMode: 'chat' | 'audit' | 'analysis') => {
      dispatch(setSidebarMode(newMode));
      // Clear highlighted section when manually switching modes
      dispatch(clearHighlightedSection());
    },
    [dispatch],
  );

  // Handle chat onboarding overlay dismiss
  const handleChatOnboardingDismiss = useCallback(
    async (method: 'manual' | 'auto') => {
      // Mark onboarding as completed in backend
      await updateOnboardingStep('note_ai_chat_onboarding_completed', true);

      // Hide overlay in UI
      dispatch(dismissChatOnboarding());

      // Track overlay dismissed event
      if (noteId) {
        noteMetrics.aiChatOnboardingDismissed(noteId, {
          encounter_id: encounterId,
          dismiss_method: method,
        });
      }
    },
    [dispatch, updateOnboardingStep, noteId, noteMetrics, encounterId],
  );

  // Handle mobile drawer close - must be defined before early returns
  const handleMobileDrawerClose = useCallback(() => {
    dispatch(setChatVisibility(false));
  }, [dispatch]);

  // Mobile drawer height state (lifted from MobileChatDrawer for layout coordination)
  const [mobileDrawerHeight, setMobileDrawerHeight] = useState<number>(
    MOBILE_DRAWER_DIMENSIONS.DEFAULT_HEIGHT_VH,
  );

  const handleMobileDrawerHeightChange = useCallback((height: number) => {
    setMobileDrawerHeight(height);
  }, []);

  // Memoized mobile drawer content (same content as desktop but in drawer)
  // Must be defined before early returns to follow rules of hooks
  const mobileChatContent = useMemo(
    () => (
      <NoteChat
        noteId={noteId || ''}
        encounterId={encounterId}
        messages={messages}
        activities={activities}
        assistantStatus={assistantStatus}
        streamingContent={streamingContent}
        firstTokenReceived={firstTokenReceived}
        currentStep={currentStep}
        error={error}
        interrupt={interrupt}
        onSendMessage={sendMessage}
        onSelectOptions={selectOptions}
        onConfirm={handleApproval}
        onExpressInterest={submitExpressInterest}
        onTextResponse={submitTextResponse}
        approvalNavigation={affectedSectionIds.length > 0 ? approvalNavigation : undefined}
        isProcessing={assistantStatus === 'thinking'}
        isOnboardingVisible={isChatOnboardingVisible}
        templatePermissions={noteData?.template?.template_permissions}
        hideHeader
      />
    ),
    [
      noteId,
      encounterId,
      messages,
      activities,
      assistantStatus,
      streamingContent,
      firstTokenReceived,
      currentStep,
      error,
      interrupt,
      sendMessage,
      selectOptions,
      handleApproval,
      submitExpressInterest,
      submitTextResponse,
      affectedSectionIds,
      approvalNavigation,
      isChatOnboardingVisible,
    ],
  );

  const mobileAuditContent = useMemo(
    () => (
      <NoteAuditSidebar
        noteId={noteId}
        encounterId={encounterId}
        currentTemplateId={getTemplateId(noteData)}
        currentTemplateName={getTemplateName(noteData)}
        audit={audit}
        onScrollToSection={handleScrollToSection}
        onFixWithAI={handleFixWithAI}
        onUseSuggestedCodes={handleUseSuggestedCodes}
        onCreateClaim={
          hasBillingFeature && noteData?.is_signed && noteData?.patient_id && encounterId
            ? handleCreateClaim
            : undefined
        }
      />
    ),
    [
      noteId,
      encounterId,
      noteData,
      audit,
      handleScrollToSection,
      handleFixWithAI,
      handleUseSuggestedCodes,
      hasBillingFeature,
      handleCreateClaim,
    ],
  );

  const mobileAnalysisContent = useMemo(
    () =>
      encounterAnalysisData ? (
        <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
          <EncounterAnalysisSidebar encounterAnalysis={encounterAnalysisData} />
        </Box>
      ) : null,
    [encounterAnalysisData],
  );

  // Handle error state - show user-friendly error message for 404 and other errors
  if (isNoteError) {
    const is404 = (noteError as any)?.status === 404;

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h6" color="error">
          {is404 ? 'Note Not Found' : 'Failed to Load Note'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {is404
            ? 'The note you are looking for does not exist or you do not have access to it.'
            : (noteError as any)?.message || 'An error occurred while loading the note.'}
        </Typography>
        <Button variant="contained" onClick={() => navigate('/notes')}>
          Back to Notes
        </Button>
      </Box>
    );
  }

  // Show loading spinner while note OR encounter data is loading
  // We wait for encounter data to ensure tabs (other notes, jotdocs) are ready
  const isLoading = isNoteLoading || (encounterId && encounterLoading);
  if (isLoading) {
    return <CenteredSpinner />;
  }

  // Create transcript content when in transcript mode
  const transcriptContent =
    isTranscriptMode && noteData?.transcript ? (
      <NoteSectionContainer>
        {/* Notes section for text_notes_string */}
        {(noteData as any)?.text_notes_string && (
          <NotepadBox marginLinePosition="35px" contentPaddingLeft={5.5} sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: 'textColors.darkHeader',
              }}
            >
              Notes
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'textColors.primaryText',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6,
              }}
            >
              {(noteData as any)?.text_notes_string}
            </Typography>
          </NotepadBox>
        )}
        <Transcript
          transcript={noteData.transcript}
          onCopy={(copiedText, copyMethod) => {
            if (!noteId || !noteData?.transcript) return;

            // Calculate total transcript character count
            const totalLength = noteData.transcript.map(segment => segment.text).join('').length;

            // Calculate percentage of transcript copied
            const textPercent = totalLength > 0 ? (copiedText.length / totalLength) * 100 : 0;

            // Track transcript text copied event
            noteMetrics.transcriptTextCopied(noteId, {
              text_length: copiedText.length,
              text_percent: Math.round(textPercent * 100) / 100,
              copy_method: copyMethod,
              encounter_id: encounterId,
            });
          }}
        />
      </NoteSectionContainer>
    ) : undefined;

  // Create encounter analysis content when in analysis mode (for tab view)
  // Note: We intentionally skip NoteSectionContainer here so the analysis card
  // can use the full available width instead of the narrow ~864px note column.
  const encounterAnalysisContent =
    isEncounterAnalysisMode && encounterAnalysisData ? (
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 3, pb: 12, width: '100%' }}>
        <EncounterAnalysisSummary encounterAnalysis={encounterAnalysisData} />
      </Box>
    ) : undefined;

  // Render appropriate TopBar based on view mode
  const renderTopBar = () => {
    // Transcript mode takes precedence over other view modes
    if (isTranscriptMode) {
      return (
        <NoteTopBarTranscript
          noteId={noteId || ''}
          encounterId={encounterId}
          onViewNote={() => {
            exitTranscriptMode();
            exitEncounterAnalysisMode();
          }}
          onDeleteNote={handleDeleteNote}
          encounterData={encounterData}
        />
      );
    }

    // Encounter analysis mode
    if (isEncounterAnalysisMode) {
      return (
        <NoteTopBarTranscript
          noteId={noteId || ''}
          encounterId={encounterId}
          onViewNote={() => {
            exitTranscriptMode();
            exitEncounterAnalysisMode();
          }}
          onDeleteNote={handleDeleteNote}
          encounterData={encounterData}
        />
      );
    }

    if (viewMode === 'completed') {
      return (
        <NoteTopBarCompleted
          noteId={noteId || ''}
          encounterId={encounterId}
          isSigned={noteData?.is_signed ?? undefined}
          hasNoteSigning={hasNoteSigning}
          hasPatient={hasPatient}
          onAmendNote={handleAmendNote}
          onDeleteNote={handleDeleteNote}
          isAmending={isUpdatingNote}
          selectedSectionsCount={selectedSectionIds.length}
          sections={sections}
          onCopyAllSections={copyAllSections}
          onCopySelectedSections={copySelectedSections}
          onCopyPasteSettings={handleCopyPasteSettings}
          onDownloadSignedPdf={handleDownloadSignedPdf}
          completionTimestamp={noteData?.completion_timestamp ?? undefined}
          patientId={encounterData?.patient_id ?? undefined}
        />
      );
    }

    // Default: edit mode
    return (
      <NoteTopBarEdit
        noteId={noteId || ''}
        currentTemplateId={getTemplateId(noteData)}
        currentTemplateName={getTemplateName(noteData)}
        encounterId={noteData?.encounter?.encounter_id ?? undefined}
        canEditTemplate={noteData?.can_edit_template ?? undefined}
        canRegenerate={noteData?.can_regenerate ?? true}
        sectionsCount={Array.isArray(sections) ? sections.length : 0}
        selectedSectionsCount={selectedSectionIds.length}
        sections={sections}
        onDeleteNote={handleDeleteNote}
        onFindReplace={handleFindReplace}
        onCopyPasteSettings={handleCopyPasteSettings}
        onCopyAllSections={copyAllSections}
        onCopySelectedSections={copySelectedSections}
        onSignNote={handleSignNote}
        hasNoteSigning={hasNoteSigning}
        hasPatient={hasPatient}
        isSigning={isUpdatingNote}
        onOpenBillingPopover={(anchorEl) => handleOpenBillingPopover(anchorEl, 'attestations', true)}
        encounterData={encounterData}
        auditIssueCount={totalIssueCount}
        isAuditEnabled={isAuditEnabled}
      />
    );
  };

  // Create tabs navigation when transcript, encounter analysis, other notes, or jotdocs are available
  const noteTabs =
    hasTranscript || allEncounterNotes.length > 0 || jotdocs.length > 0 ? ( // TODO: Add back hasEncounterAnalysis when ready
      <NoteTabs
        value={activeTabValue}
        onChange={handleTabChange}
        hasTranscript={hasTranscript}
        // hasEncounterAnalysis={hasEncounterAnalysis} // TODO: Uncomment when ready to show
        currentNoteTitle={currentNoteTitle}
        allNotes={allEncounterNotes.length > 1 ? allEncounterNotes : undefined}
        otherNotes={otherNotes} // Keep for backwards compatibility
        jotdocs={jotdocs}
      />
    ) : undefined;

  // Render desktop sidebar content
  const renderSidebarContent = () => {
    // Chat content (with header hidden, as SidebarContainer provides the header)
    const chatContentInner = (
      <NoteChat
        noteId={noteId || ''}
        encounterId={encounterId}
        messages={messages}
        activities={activities}
        assistantStatus={assistantStatus}
        streamingContent={streamingContent}
        firstTokenReceived={firstTokenReceived}
        currentStep={currentStep}
        error={error}
        interrupt={interrupt}
        onSendMessage={sendMessage}
        onSelectOptions={selectOptions}
        onConfirm={handleApproval}
        onExpressInterest={submitExpressInterest}
        onTextResponse={submitTextResponse}
        approvalNavigation={affectedSectionIds.length > 0 ? approvalNavigation : undefined}
        isProcessing={assistantStatus === 'thinking'}
        isOnboardingVisible={isChatOnboardingVisible}
        templatePermissions={noteData?.template?.template_permissions}
        hideHeader
      />
    );

    // Audit content
    const auditContentInner = (
      <NoteAuditSidebar
        noteId={noteId}
        encounterId={encounterId}
        currentTemplateId={getTemplateId(noteData)}
        currentTemplateName={getTemplateName(noteData)}
        audit={audit}
        onScrollToSection={handleScrollToSection}
        onFixWithAI={handleFixWithAI}
        onUseSuggestedCodes={handleUseSuggestedCodes}
        onCreateClaim={
          hasBillingFeature && noteData?.is_signed && noteData?.patient_id && encounterId
            ? handleCreateClaim
            : undefined
        }
      />
    );

    // Analysis content
    const analysisContentInner = encounterAnalysisData ? (
      <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
        <EncounterAnalysisSidebar encounterAnalysis={encounterAnalysisData} />
      </Box>
    ) : null;

    // Use SidebarContainer for unified header with toggle
    return (
      <SidebarContainer
        mode={sidebarMode}
        onModeChange={handleSidebarModeChange}
        showToggle={isAuditEnabled}
        auditIssueCount={totalIssueCount}
        hasMessages={messages.length > 0}
        onClearChat={handleClearChat}
        chatContent={chatContentInner}
        auditContent={auditContentInner}
        analysisContent={analysisContentInner}
        showAnalysisMode={hasEncounterAnalysis && isEncounterAnalysisSidebarEnabled}
        noteId={noteId}
        encounterId={encounterId}
      />
    );
  };

  return (
    <>
      <NoteChatLayout
        chat={renderSidebarContent()}
        chatVisible={chatVisible}
        topBar={renderTopBar()}
        metadata={
          <>
            <NoteMetadata
              noteId={noteId || ''}
              noteData={noteData}
              encounterData={encounterData}
              editable={isEditable}
              suggestedCptCodes={audit.cptAnalysis?.recommendedCptCodes ?? undefined}
              onUseSuggestedCodes={
                audit.cptAnalysis?.recommendedCptCodes?.length
                  ? async () => {
                      // Clean CPT codes before applying
                      const { cleanCptCodes } = await import('@components/ui/forms/CPTCodePicker');
                      const codes = cleanCptCodes(
                        audit.cptAnalysis?.recommendedCptCodes?.filter(c => c && c.trim()) || [],
                      );
                      if (codes.length > 0) {
                        handleUseSuggestedCodes(codes, 'metadata_bar');
                      }
                    }
                  : undefined
              }
              onViewLastEncounterNote={handleViewLastEncounterNote}
              onCptCodesChanged={handleCptCodesChanged}
              onOpenBillingPopover={(anchorEl, section) => handleOpenBillingPopover(anchorEl, section ?? 'billing')}
            />
            {/* Banner to add template to user's list */}
            <AddTemplateToListBanner noteData={noteData} />
            {/* Banner when transcripts have been deleted */}
            {encounterData?.transcripts_deleted && (
              <Alert severity="info" sx={{ mx: 2, mt: 1 }}>
                Transcripts have been deleted. This note cannot be regenerated.
              </Alert>
            )}
          </>
        }
        tabs={noteTabs}
        noteTitle={
          <NoteTitleEditor noteId={noteId || ''} noteData={noteData} editable={isEditable} />
        }
        noteSections={
          <>
            {/* Note generation progress indicator (v2 streaming flow) */}
            <Fade in={showGenerationView} timeout={400} unmountOnExit>
              <Box sx={{ mt: 3, mb: 2, mx: 2 }}>
                <NoteGenerationProgress
                  currentStep={generationWorkflowStep}
                  completedSteps={generationCompletedSteps}
                  workflowNodes={generationWorkflowNodes}
                  isComplete={isGenerationComplete}
                  hasError={Boolean(generationStreamingError)}
                  variant="full"
                  encounterAnalysis={encounterAnalysis}
                />
              </Box>
            </Fade>
            <NoteSectionsList
              sections={sections}
              affectedSectionIds={affectedSectionIds}
              sectionUpdates={sectionUpdates}
              streamingSections={streamingSections}
              edits={interrupt?.edits}
              onSectionChange={handleSectionChange}
              encounterId={noteData?.encounter?.encounter_id ?? undefined}
              editable={isEditable}
              onApprove={handleApprove}
              onRevert={handleRevert}
              previousContent={previousContent}
              navigation={affectedSectionIds.length > 0 ? approvalNavigation : undefined}
              jotcoachEncounterSession={encounterSession}
              noteId={noteId}
              onSaveStart={onSaveStart}
              onMarkDirty={markDirty}
              sectionIssueMap={sectionIssueMap}
              generationStreamingSections={
                showGenerationView ? generationStreamingSections : undefined
              }
              isGenerating={isGeneratingNote}
              onEditorsReady={handleEditorsReady}
            />
          </>
        }
        noteToc={
          <NoteTOC
            sections={sections}
            containerSelector={LAYOUT_SELECTORS.NOTE_SECTIONS_CONTAINER}
            onCopySelected={copySelectedSections}
            noteId={noteId}
            encounterId={noteData?.encounter?.encounter_id ?? undefined}
          />
        }
        noteTocInline={
          <NoteTOCInline
            sections={sections}
            containerSelector={LAYOUT_SELECTORS.NOTE_SECTIONS_CONTAINER}
            onCopySelected={copySelectedSections}
            noteId={noteId}
            encounterId={noteData?.encounter?.encounter_id ?? undefined}
          />
        }
        alternativeContent={transcriptContent || encounterAnalysisContent}
        scrollContainerId={LAYOUT_SELECTORS.NOTE_SECTIONS_CONTAINER.replace('#', '')}
        overlay={
          isChatOnboardingVisible && (
            <ChatDiscoverabilityOverlay onDismiss={handleChatOnboardingDismiss} />
          )
        }
        mobileDrawerOpen={showMobileDrawer && isChatVisible}
        mobileDrawerHeight={mobileDrawerHeight}
      />

      {/* Note Signing Onboarding Dialog */}
      <SigningNoteOnboarding
        open={isOnboardingOpen}
        onClose={() => setIsOnboardingOpen(false)}
        onComplete={handleOnboardingComplete}
      />

      {/* ElevenLabs Voice Widget */}
      {/* <ElevenLabsWidget compact position="left" /> */}

      {/* Mobile Chat Drawer - shown on narrow screens */}
      {showMobileDrawer && (
        <MobileChatDrawer
          open={isChatVisible}
          onClose={handleMobileDrawerClose}
          drawerHeight={mobileDrawerHeight}
          onHeightChange={handleMobileDrawerHeightChange}
          mode={sidebarMode}
          onModeChange={handleSidebarModeChange}
          showToggle={isAuditEnabled}
          auditIssueCount={totalIssueCount}
          hasMessages={messages.length > 0}
          onClearChat={handleClearChat}
          chatContent={mobileChatContent}
          auditContent={mobileAuditContent}
          analysisContent={mobileAnalysisContent}
          showAnalysisMode={hasEncounterAnalysis && isEncounterAnalysisSidebarEnabled}
          noteId={noteId}
          encounterId={encounterId}
        />
      )}

      {/* Unified Billing Popover (replaces AttestationsPopover) */}
      {encounterData && (
        <SessionBillingPopover
          open={billingPopoverOpen}
          anchorEl={billingPopoverAnchor}
          onClose={handleCloseBillingPopover}
          focusSection={billingPopoverSection}
          modality={(encounterData.modality as Modality | null) ?? null}
          onModalityChange={handleBillingModalityChange}
          placeOfService={extractPlaceOfService(encounterData.working_billing_claim)}
          onPlaceOfServiceChange={handleBillingPlaceOfServiceChange}
          duration={
            encounterData.start_time && encounterData.end_time
              ? Math.floor(
                  (new Date(encounterData.end_time).getTime() -
                    new Date(encounterData.start_time).getTime()) /
                    (1000 * 60),
                )
              : encounterData.appointment?.duration ?? null
          }
          onDurationChange={handleBillingDurationChange}
          startTime={encounterData.start_time ?? encounterData.appointment?.start_time ?? null}
          endTime={encounterData.end_time ?? null}
          encounterDate={encounterData.start_time ?? encounterData.appointment?.start_time ?? null}
          onTimeChange={handleBillingTimeChange}
          cptCodes={extractBillingCodes(encounterData.working_billing_claim)}
          onCptCodesChange={handleBillingCptCodesChange}
          icd10Codes={extractDiagnosisCodes(encounterData.working_billing_claim)}
          onIcd10CodesChange={handleBillingIcd10CodesChange}
          attestations={(encounterData.attestations ?? {}) as Partial<EncounterAttestations>}
          onAttestationChange={handleBillingAttestationChange}
          hasPatient={hasPatient}
          editable={!isUpdatingNote}
          showSignButton={billingPopoverShowSign}
          onSignNote={handleSignNote}
          isSigning={isUpdatingNote}
          hasNoteSigning={hasNoteSigning}
        />
      )}
    </>
  );
};
